import { nonnull } from "@tento-core/assert";
import {
  CalendarReadRevision,
  type CalendarReadValue,
} from "@tento-lona/sheets-ui/sheet/calendar-read-revision.ts";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { CalendarColumn } from "@tento-ui/components/calendar/calendar-column";
import { CalendarColumnCell } from "@tento-ui/components/calendar/calendar-column-cell";
import { CalendarDay } from "@tento-ui/components/calendar/calendar-day";
import {
  type CalendarSubcolumn,
  type CalendarSubcolumnSpan,
  type SubcolumnCreateHandler,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { type Option } from "@tento-lona/sheets";
import { type RoughBox, RoughLayer } from "./calendar-rough-layer.ts";
import type { RowLocalId } from "@tento-lona/sheets";
import { $$ } from "@tento-ui/fastdom";
import { ZIndex } from "@tento-ui/z-index";
import {
  DateFragment,
  DateRegion,
  DateTime,
  Duration,
  GenericRange,
  NaiveDate,
  NaiveDateTime,
  Time,
  TimeOfDay,
  TimePoint,
  Utc,
  Weekday,
} from "@tento-chrono";
import { EventGhost } from "@tento-lona/sheets-ui/sheet/gestures/event-ghost.ts";
import { type Movable } from "@tento-lona/sheets-ui/sheet/gestures/interactable-item.ts";
import { EventItemPresentation } from "@tento-lona";
import type { SheetRow } from "@tento-lona";
import { CalendarSheetColumn } from "./calendar-sheet-column.ts";
import { CurrentTimeIndicator } from "@tento-ui/components/calendar/current-time-indicator";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { Rational } from "@tento-core/rational";
import { MoreIndicator } from "../../../renderer/timeline-row-event/all-day-events/more-indicator.ts";
import {
  CALENDAR_DAYS_ROW_INSTRUMENTATION_KEY,
  type CalendarDaysStripInstrumentation,
} from "../../../../instrumentation.ts";
import {
  attachStripObserver,
  type StripObserverList,
} from "./calendar-days-strip-observers.ts";
import {
  StripBindState,
  type StripBindHost,
  StripKey,
  bindStrip,
  boundProps,
  clearStripRead,
  parkStrip,
  recycleStripBind,
  retireStripBind,
  supersedeStripBind,
} from "./calendar-days-strip-bind.ts";
import {
  DragGhostState,
  type DragGhostHost,
  onExistingGhost,
  setDragGhost,
} from "./calendar-days-strip-drag-ghost.ts";
import { SpanContexts } from "./calendar-days-strip-span-contexts.ts";
import { StripSpanPaint } from "./calendar-days-strip-paint.ts";
import { clearPendingCell, paintPendingCell } from "./calendar-days-strip-pending.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

/** `CalendarDaysStrip`'s friend record (`Internals`): its constructor registers each instance. */
type CalendarDaysStripInternals = {
  readonly instrumentation: (row: CalendarDaysStrip) => CalendarDaysStripInstrumentation;
};
const calendarDaysStripInternals = new Internals<CalendarDaysStrip, CalendarDaysStripInternals>("CalendarDaysStrip");

/**
 * The seam onto a row no constructor ran on (an unbound prototype instance):
 * no props, no theme, no registration, generation 0 and no span layers, and
 * it never binds or paints (an observer attached to it never runs). The
 * drive verbs (`paintSpans`, `applyExistingGhost`, `replaceSpanContexts`,
 * `setDragGhost`, `clearCompletedRead`) need a constructed row and throw a
 * `TypeError`.
 */
const UNBOUND_STRIP_INSTRUMENTATION: CalendarDaysStripInstrumentation = Object.freeze({
  boundProps: () => null,
  themeBackground: () => null,
  hasPendingRenderer: () => false,
  dropPendingRenderer: () => {},
  bindGeneration: () => 0,
  onBind: () => () => {},
  onPaint: () => () => {},
  onRenderRequest: () => () => {},
  spanContexts: () => new Map(),
  replaceSpanContexts: () => unboundStrip("replaceSpanContexts"),
  dragGhost: () => null,
  setDragGhost: () => unboundStrip("setDragGhost"),
  paintSpans: () => unboundStrip("paintSpans"),
  applyExistingGhost: () => unboundStrip("applyExistingGhost"),
  clearCompletedRead: () => unboundStrip("clearCompletedRead"),
});

function unboundStrip(verb: string): never {
  throw new TypeError(`CalendarDaysStrip: ${verb} needs a constructed row`);
}

/** The seam onto `row`: its own for a constructed row, else the unbound one. */
function stripInstrumentation(row: CalendarDaysStrip): CalendarDaysStripInstrumentation {
  return calendarDaysStripInternals.peek(row)?.instrumentation(row) ?? UNBOUND_STRIP_INSTRUMENTATION;
}

/**
 * The test-harness seam onto `row` (see `CalendarDaysStripInstrumentation`).
 *
 * @internal Not on the `@tento/lona/sheets-ui` barrel; tests import
 * `calendarDaysStripInstrumentationOf` from the workspace-only
 * `@tento-lona/sheets-ui/internal-testing`.
 */
export function calendarDaysStripInstrumentationOf(
  row: CalendarDaysStrip,
): CalendarDaysStripInstrumentation {
  return stripInstrumentation(row);
}

/**
 * Install the `Symbol.for` harness door onto `CalendarDaysStrip.prototype`
 * (idempotent; opt-in through `installUiHarnessDoors`,
 * `@tento-lona/sheets-ui/internal-testing`).
 */
export function installCalendarDaysStripInstrumentationDoor(): void {
  const proto = CalendarDaysStrip.prototype;
  if (Object.prototype.hasOwnProperty.call(proto, CALENDAR_DAYS_ROW_INSTRUMENTATION_KEY)) return;
  Object.defineProperty(proto, CALENDAR_DAYS_ROW_INSTRUMENTATION_KEY, {
    value: function calendarDaysRowInstrumentationDoor(
      this: CalendarDaysStrip,
    ): CalendarDaysStripInstrumentation {
      return stripInstrumentation(this);
    },
  });
}

export type SheetNamedChildren = {
  pending: CalendarColumnCell.Basic;
  timeIndicator: CurrentTimeIndicator;
  bottomShadow: CalendarDaysStripShadow;
  topShadow: CalendarDaysStripShadow;
  moreAbove: MoreIndicator;
  moreBelow: MoreIndicator;
};

type RefCount = {
  pending: number;
  events: number;
};


export type ColumnFragment<NamedChildren extends Record<string, HTMLElement>> = {
  id: "before" | "after";
  otherId: "before" | "after";
  frag: Option<DateFragment>;
  $frag: CalendarColumn<NamedChildren>;
  topShadowRefCount: RefCount;
  bottomShadowRefCount: RefCount;
};

export type DaysStripColumn<NamedChildren extends Record<string, HTMLElement>> = {
  dr: DateRegion;
  $day: CalendarDay<NamedChildren>;
  frags: ColumnFragment<NamedChildren>[];
  subindex: Rational;
};

/** The days of a calendar row, drawn as day columns with their events. */
@component({
  name: "lona-calendar-days-row",
})
export class CalendarDaysStrip extends LonaWebComponent {
  /** A calendar days row; registers its seam (`calendarDaysStripInstrumentationOf`). */
  constructor() {
    super();
    // A decorated class holds no static `#` field (TS18036): its record is built here.
    calendarDaysStripInternals.register(this, {
      instrumentation: (row) => ({
        boundProps: () => row.#bind.props,
        themeBackground: () => row.#boundDeps?.theme.backgroundColor ?? null,
        hasPendingRenderer: () => row.#pendingDisposer != null,
        dropPendingRenderer: () => {
          row.#pendingDisposer?.();
          row.#pendingDisposer = null;
        },
        bindGeneration: () => row.#bind.generation,
        onBind: (observer) =>
          attachStripObserver({
            get: () => row.#bindObservers,
            set: (list) => {
              row.#bindObservers = list;
            },
          }, observer),
        onRenderRequest: (observer) =>
          attachStripObserver({
            get: () => row.#renderRequestObservers,
            set: (list) => { row.#renderRequestObservers = list; },
          }, observer),
        onPaint: (observer) =>
          attachStripObserver({
            get: () => row.#paintObservers,
            set: (list) => {
              row.#paintObservers = list;
            },
          }, observer),
        spanContexts: () => row.#spans.view,
        replaceSpanContexts: (next) => row.#spans.replace(next),
        dragGhost: () => row.#ghost.current,
        setDragGhost: (ghost) => setDragGhost(row.#ghost, ghost),
        paintSpans: ($layout, ctx) => row.#paint.paint($layout, ctx),
        applyExistingGhost: (ghost, settle) => onExistingGhost(row.#ghostHost, row.#ghost, ghost, settle === "revert"),
        clearCompletedRead: () => clearStripRead(row.#bind),
      }),
    });
  }

  #$days: CalendarDay<SheetNamedChildren>[] = [];

  /**
   * What the strip is waiting for: `"loading"` while its column loads,
   * `"events"` while a bind's provider reads are outstanding, `null` once the
   * bind revealed (succeeded or failed) or the strip recycled. Observational
   * only (the native strip fixture reads it); nothing here gates on it.
   */
  binding: Option<"loading" | "events"> = null;
  #presentationEnd: Option<() => void> = null;

  onPresentationEnd(callback: () => void): void {
    this.#presentationEnd = callback;
  }

  /** Where this strip's rough boxes paint (`calendar-rough-layer`); null until its column hands it a layer. */
  #rough: Option<RoughLayer> = null;

  /** Paint this strip's rough boxes into `$layer`, its calendar column's rough layer (`calendar-rough-layer`). */
  adoptRoughLayer($layer: HTMLElement): void {
    this.#rough = new RoughLayer($layer);
  }

  /** Remove this strip's rough boxes (the column's presentation end, a new date's load, a recycle). */
  clearRough(): void {
    this.#rough?.clear();
  }

  /** Whether the strip's column is parked in the scroll pool now (set by its calendar column). */
  #inPool: () => boolean = () => false;

  /** Tell the strip how to ask whether its column is parked in the scroll pool. */
  onPoolQuery(query: () => boolean): void {
    this.#inPool = query;
  }
  /** This strip's identity as a key: the work queue, clock listener and content-lag meter key it, never `this`. */
  readonly #key = new StripKey();
  /** The bind's state (assigned only by calendar-days-strip-bind.ts). */
  readonly #bind = new StripBindState();
  /** What the bind half reaches on this strip. */
  readonly #bindHost: StripBindHost = Object.freeze({
    key: this.#key,
    deps: () => this.#deps,
    signals: () => this.#signals,
    columns: () => this.columns(),
    resetColumns: () => {
      this.#cache = {};
    },
    cachedDateCount: () => this.#cache.dates?.length ?? 0,
    visitColumns: (visit: (info: DaysStripColumn<SheetNamedChildren>) => void) => this.#visitColumns(visit),
    dragGhost: () => this.#ghost.current,
    notifyBind: (props: CalendarDaysStrip.Props, generation: number) => this.#bindObservers?.notify(
      Object.freeze({ row: this, props, generation }),
    ) ?? null,
    connected: () => this.isConnected,
    pooled: () => this.#inPool(),
    setBinding: (binding: CalendarDaysStrip["binding"]) => {
      this.binding = binding;
    },
    showContent: () => {
      this.style.opacity = "1";
    },
    endPresentation: () => this.#presentationEnd?.(),
    paintRough: (boxes: readonly RoughBox[]) => this.#rough?.paint(boxes),
    contentHidden: () => this.style.opacity === "0",
    setWindowOffset: (hours: string) => this.style.setProperty("--calendar-column-window-offset-hrs", hours),
    listening: () => this.#pendingDisposer != null && this.#draftPreviewDisposer != null,
    listen: () => {
      if (this.#pendingDisposer == null) {
        this.#pendingDisposer = this.#signals.addPendingEventRenderer(
          (ghost, settle) => this.#bindPendingEvent(ghost, settle === "revert"),
        );
      }
      if (this.#draftPreviewDisposer == null) {
        this.#draftPreviewDisposer = this.#deps.presentation.drafts.onChange(
          () => this.#repaintSpanLayers(),
        );
      }
    },
    renderSpans: (...args: Parameters<StripSpanPaint["render"]>) => {
      const after = this.#renderRequestObservers?.notify(Object.freeze({
        row: this, generation: this.#bind.generation,
      })) ?? null;
      try {
        return this.#paint.render(...args);
      } finally {
        after?.();
      }
    },
  });

  #boundDeps: Option<CalendarSheetColumn.Dependencies> = null;
  #boundSignals: Option<CalendarSheetColumn.Signals> = null;

  /** The harness's bind observers (`onBind` on the instrumentation seam); `null` while none is attached. */
  #bindObservers: Option<StripObserverList<CalendarDaysStripInstrumentation.BindEvent>> = null;
  /** The harness's paint observers (`onPaint` on the instrumentation seam); `null` while none is attached. */
  #paintObservers: Option<StripObserverList<CalendarDaysStripInstrumentation.PaintEvent>> = null;
  #renderRequestObservers: Option<StripObserverList<CalendarDaysStripInstrumentation.RenderRequestEvent>> = null;

  /** The dependencies `init` set (a read before it is a caller bug). */
  get #deps(): CalendarSheetColumn.Dependencies {
    return nonnull(this.#boundDeps, "CalendarDaysStrip: dependencies read before init");
  }

  /** The signals `init` set (a read before it is a caller bug). */
  get #signals(): CalendarSheetColumn.Signals {
    return nonnull(this.#boundSignals, "CalendarDaysStrip: signals read before init");
  }

  /** The props the last `bind` set (a read before it is a caller bug). */
  get #props(): CalendarDaysStrip.Props {
    return boundProps(this.#bind);
  }
  #cache: {
    dates?: Readonly<NaiveDate[]>;
    drs?: Readonly<DateRegion[]>;
    columns?: Readonly<DaysStripColumn<SheetNamedChildren>[]>;
  } = {};
  dates(): Readonly<NaiveDate[]> {
    if (this.#cache.dates != null) return this.#cache.dates;
    return (this.#cache.dates = this.#props.date[0].toRange().iter());
  }

  drs(): Readonly<DateRegion[]> {
    if (this.#cache.drs != null) return this.#cache.drs;
    const tz = this.#props.date[1];
    const dates = this.dates();
    const drs = dates.map((date) => new DateRegion(date, tz));
    return (this.#cache.drs = drs);
  }

  columns(): Readonly<DaysStripColumn<SheetNamedChildren>[]> {
    if (this.#cache.columns) return this.#cache.columns;
    const drs = this.drs();
    const entries = [...drs.entries()];
    const numEntries = entries.length;
    const columns: DaysStripColumn<SheetNamedChildren>[] = entries.map(
      ([idx, dr]) => {
        const $day = nonnull(this.#$days[idx], "CalendarDaysStrip: a day element per date");
        this.#signals.currentGestureCount.addListener($day, (cnt) => {
          $day.bindNoGesturesActive(cnt === 0);
        });

        const [before = null, after = null] = dr.dateFragments();
        const frags = [
          {
            id: "before" as const,
            otherId: "after" as const,
            frag: before,
            $frag: $day.$before,
            topShadowRefCount: {
              pending: 0,
              events: 0,
            },
            bottomShadowRefCount: {
              pending: 0,
              events: 0,
            },
          },
          {
            id: "after" as const,
            otherId: "before" as const,
            frag: after,
            $frag: $day.$after,
            topShadowRefCount: {
              pending: 0,
              events: 0,
            },
            bottomShadowRefCount: {
              pending: 0,
              events: 0,
            },
          },
        ];

        return {
          dr,
          $day,
          frags,
          subindex: new Rational(idx, numEntries),
        };
      },
    );
    return (this.#cache.columns = columns);
  }

  init(
    deps: CalendarSheetColumn.Dependencies,
    signals: CalendarSheetColumn.Signals,
  ) {
    this.#boundDeps = deps;
    this.#boundSignals = signals;
    for (const $day of this.#$days)
      $day.init(
        this.#boundDeps.theme.backgroundColor,
        deps.gestureManager.asEmptyDelegate(),
      );
  }

  #pendingDisposer: Option<() => void> = null;
  #draftPreviewDisposer: Option<() => void> = null;

  bind(props: CalendarDaysStrip.Props) {
    bindStrip(this.#bindHost, this.#bind, props);
  }

  #repaintSpanLayers(): void {
    if (!this.#bind.props) return;
    this.#spans.forEachLive(($layout, ctx) => {
      this.#paint.paint($layout, ctx);
    });
  }

  /** The in-flight drag ghost (assigned only by calendar-days-strip-drag-ghost.ts). */
  readonly #ghost = new DragGhostState();
  /** What the drag ghost reaches on this strip. */
  readonly #ghostHost: DragGhostHost = Object.freeze({
    spanContexts: () => this.#spans,
    paintSpans: ($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx) => this.#paint.paint($layout, ctx),
    forEachSpanLayer: (fn: Parameters<DragGhostHost["forEachSpanLayer"]>[0]) => this.#spans.forEachLive(fn),
  });

  /** The span layers' last render contexts (written through `SpanContexts` only). */
  readonly #spans = new SpanContexts();
  /** The span paint (calendar-days-strip-paint.ts). */
  readonly #paint = new StripSpanPaint(Object.freeze({
    bind: this.#bind,
    ghost: this.#ghost,
    ghostHost: this.#ghostHost,
    contexts: this.#spans,
    deps: () => this.#deps,
    signals: () => this.#signals,
    notifyPaint: ($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx) => this.#paintObservers?.notify(
      Object.freeze({
        row: this,
        layout: $layout,
        ctx,
        generation: this.#bind.generation,
        hasDragGhost: this.#ghost.current != null,
      }),
    ) ?? null,
  }));

  toggleLoading(isLoading: boolean, _column: NaiveDate.Partial.WithTz) {
    this.toggleAttribute("show-list-loading", false);
    if (!isLoading) return;
    supersedeStripBind(this.#bindHost, this.#bind, this.#boundDeps?.stripWork ?? null);
    this.#boundDeps?.contentLag?.hidden(this.#key);
    this.#boundDeps?.contentLag?.unloaded?.(this.#key);
    this.binding = "loading";
    this.style.opacity = "0";
  }

  /**
   * Binds a pending event to the calendar view, handling timezone transitions through DateFragments.
   * This method renders an ephemeral event (like a preview of an event being created or moved).
   *
   * @param e The event ghost (pending event) to render, or null to clear
   * @param date The current date range being displayed in the calendar
   * @param tz The timezone region for contextualizing dates
   * @param events The store containing event data
   */
  #bindPendingEvent(e: Option<EventGhost>, revert: boolean) {
    if (!this.#bind.props) return;

    // Existing-item drags promote through `renderSpans` (the ghost span):
    // same-day reuses the real cell, cross-day injects a metadata span. The
    // bespoke pending cell below is only for the new-event create ghost.
    if (e?.type === "existing") {
      this.#clearPendingCell();
      onExistingGhost(this.#ghostHost, this.#ghost, {
        id: e.item.id,
        range: e.newTimeRange,
        item: e.item,
      }, revert);
      return;
    }

    // null or "new": no existing-item ghost in flight.
    onExistingGhost(this.#ghostHost, this.#ghost, null, revert);

    if (!e) {
      this.#clearPendingCell();
      return;
    }

    paintPendingCell(e, {
      columns: this.columns(),
      props: this.#bind.props,
      deps: this.#deps,
      redrawShadow: (columnFrag) => this.#redrawFragmentShadow(columnFrag),
      bindPendingCell: CalendarDaysStrip.bindPendingCell,
    });
  }

  /** Remove the new-event create pending cell + its shadows from every day. */
  #clearPendingCell(): void {
    clearPendingCell(this.columns(), (columnFrag) => this.#redrawFragmentShadow(columnFrag));
  }

  /**
   * Helpers
   * ===========================================================================
   */
  #redrawFragmentShadow(column: ColumnFragment<SheetNamedChildren>) {
    const window = this.#props.window;
    const colStartAtWindow =
      column.frag?.start.time.equals(window?.start) || true;
    const colEndAtWindow = column.frag?.end.time.equals(window?.end) || true;

    if (
      (column.topShadowRefCount.events > 0 ||
        column.topShadowRefCount.pending > 0) &&
      colStartAtWindow
    ) {
      column.$frag.$namedChildOrElse(
        "topShadow",
        CalendarDaysStripShadow.makeTop,
      );
    } else {
      column.$frag.removeNamedChild("topShadow");
    }
    if (
      (column.bottomShadowRefCount.events > 0 ||
        column.bottomShadowRefCount.pending > 0) &&
      colEndAtWindow
    ) {
      column.$frag.$namedChildOrElse(
        "bottomShadow",
        CalendarDaysStripShadow.makeBottom,
      );
    } else {
      column.$frag.removeNamedChild("bottomShadow");
    }
  }

  /** Should be called in a mutate */
  #visitColumns(visit: (info: DaysStripColumn<SheetNamedChildren>) => void) {
    if (!this.#bind.props) return;
    const props = this.#bind.props;
    const hasWindow =
      props.partialWindow.start != null || props.partialWindow.end != null;

    const numDays = this.dates().length;

    while (this.#$days.length < numDays) {
      const $day = CalendarDay.make();
      $day.init(
        this.#boundDeps?.theme.backgroundColor,
        this.#deps.gestureManager.asEmptyDelegate(),
      );
      $day.$before.toggleAttribute("manual-layout", true);
      $day.$after.toggleAttribute("manual-layout", true);
      this.append($day);
      this.#$days.push($day);
    }

    for (const $extra of this.#$days.slice(numDays)) {
      $extra.style.display = "none";
    }

    const columns = this.columns();
    for (const [idx, column] of columns.entries()) {
      visit(column);
      column.$day.style.width = `calc((100% / ${numDays}) - 0px)`;
      column.$day.setAttribute("dbg-idx", String(idx));
      if (hasWindow) {
        column.$day.style.setProperty("--subcolumn-divider", "transparent");
      } else {
        column.$day.style.setProperty(
          "--subcolumn-divider",
          column.dr.nd.dayOfWeek !== this.#bind.props.startOfWeek
            ? "var(--divider)"
            : "var(--divider-pri)",
        );
      }
      column.$day.style.removeProperty("display");
    }
  }

  /**
   * The strip's column went back to the scroll pool and keeps its content for
   * a reattach at the same date (`scroll-column-retention`): it shows nothing
   * now, so it reports nothing shown until its next bind and its queued
   * units are dropped; its completed read, span contexts and listeners stay
   * (a reattach reuses them).
   */
  park(): void {
    parkStrip(this.#bindHost, this.#bind, this.#boundDeps?.stripWork ?? null);
    this.#boundDeps?.contentLag?.hidden(this.#key);
    this.#boundDeps?.contentLag?.unloaded?.(this.#key);
  }

  /**
   * A pooled strip may stay connected: it must not keep repainting its old
   * date. Recycling retires the bind, its span contexts (so a draft change or
   * drag broadcast has nothing to repaint), any drag ghost and the
   * broadcast subscriptions; the next `bind` registers them again.
   */
  onRecycle(): void {
    super.onRecycle();
    this.#retire();
    recycleStripBind(this.#bind);
    this.#spans.clear();
  }

  disconnectedCallback() {
    this.#retire();
    // Remove listeners from signals using $day elements as keys
    for (const $day of this.#$days) {
      this.#boundSignals?.currentGestureCount.removeListener($day);
    }
  }

  /** End the current bind and drop what would repaint it from outside. */
  #retire(): void {
    retireStripBind(this.#bindHost, this.#bind, this.#boundDeps?.stripWork ?? null);
    this.#boundDeps?.contentLag?.hidden(this.#key);
    this.#boundDeps?.contentLag?.unloaded?.(this.#key);
    this.binding = null;
    this.#pendingDisposer?.();
    this.#pendingDisposer = null;
    this.#draftPreviewDisposer?.();
    this.#draftPreviewDisposer = null;
    // Drop any in-flight drag ghost so a disconnect or recycle mid-drag
    // doesn't leave a stale ghost to be re-applied on the next bind.
    setDragGhost(this.#ghost, null);
    this.#boundSignals?.currentTime.removeListener(this.#key);
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        position: relative;
        height: calc(100% - 0px);
        display: flex;
        flex-direction: row;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`<slot></slot>`;
}

/**
 * Types and values that go with `CalendarDaysStrip`: `Props`, `DragGhost`,
 * `SpanRenderCtx`, `PendingCellViewModel`, `bindPendingCell`.
 */
export namespace CalendarDaysStrip {
  export type Props = {
    readRevision: Option<CalendarReadRevision>;
    readEnvironment: () => readonly CalendarReadValue[];
    rowId: RowLocalId;
    /**
     * The cell's bound row. Used to discover the event-capable row via
     * `findRowWithFacade(EVENTS_FACADE)` for the more-events modal.
     */
    row: SheetRow.Joined.Handle;
    date: NaiveDate.Partial.WithTz;
    window: TimeOfDay.Range;
    partialWindow: GenericRange<Option<NaiveDateTime>>;
    subcolumns: CalendarSubcolumn[];
    column: ColumnIndex.Key.Resolved.Default;
    startOfWeek: Weekday;
  };

  /** An in-flight existing-item drag: the item id, its current dragged range,
   *  and the gesture item (for cross-day metadata + gesture continuation). */
  export type DragGhost = {
    id: string;
    range: DateTime.Range<Utc>;
    item: Movable;
  };

  /** Per-`$layout` render context cached by `renderSpans` so a drag ghost
   *  change can repaint synchronously via `paintSpans`. */
  export type SpanRenderCtx = {
    spans: readonly CalendarSubcolumnSpan[];
    rowId: RowLocalId;
    column: ColumnIndex.Key.Resolved.WithSubindex;
    windowed: DateFragment.Windowed;
    createHandler: Option<SubcolumnCreateHandler>;
  };

  export type PendingCellViewModel = {
    type: "existing" | "new";
    item: Option<Movable>;
    backgroundColor: string;
    title: Option<string>;
    cursor: Option<string>;
  };

  export function bindPendingCell(
    $cell: CalendarColumnCell.Basic,
    {
      vm,
      time,
      timeDisplay,
      overlapsTop,
      overlapsBottom,
    }: {
      vm: PendingCellViewModel;
      time: TimePoint.Range;
      timeDisplay: DateTime.Range;
      overlapsTop: boolean;
      overlapsBottom: boolean;
    },
  ) {
    let renderedTime = time;
    if (renderedTime.duration.toMs < 15 * Time.MS_PER_MIN) {
      // dev(
      //   "PENDING CELL",
      //   renderedTime.start.toString(),
      //   renderedTime.end.toString(),
      // );
      renderedTime = new TimePoint.Range(
        time.start,
        time.start.add(Duration.Time.from({ mins: 15 })),
      );
    } else {
      // dev(
      //   "PENDING CELL",
      //   renderedTime.start.toString(),
      //   renderedTime.end.toString(),
      // );
    }

    if (vm.item?.id) {
      $cell.setAttribute("event-id", vm.item.id);
    } else {
      $cell.removeAttribute("event-id");
    }

    $cell.setAttribute("type", vm.type);
    $cell.toggleAttribute("editable", true);
    $cell.bind({
      id: "pending",
      time: renderedTime,
      background: vm.backgroundColor,
      loading: false,
      ignoreLayout: true,
    });
    $cell.assignStyles({
      display: "",
      cursor: vm.cursor ?? "",
      "--std-calendar-cell-x-offset": 3.5 + "px",
      "--std-calendar-cell-additional-height": "-1px",
      color: "var(--text-pri-on-solid)",
      transition: "box-shadow .15s ease",
      zIndex: String(ZIndex.STICKY),
      boxShadow: "rgba(0, 0, 0, 0.6) 0px 1px 3px 0.5px",
      borderTopRightRadius: overlapsTop ? "0px" : "",
      borderTopLeftRadius: overlapsTop ? "0px" : "",
      borderBottomRightRadius: overlapsBottom ? "0px" : "",
      borderBottomLeftRadius: overlapsBottom ? "0px" : "",
    });
    //
    // NOTE: timing issues - the cell might be recycled
    //
    // $$.deferMutate(() => {
    //   $cell.style.boxShadow = "var(--box-shadow)";
    // });

    const $content = $cell.$content();
    $content.style.removeProperty("display");
    $content.bind({
      label: vm.title ?? "Untitled",
      time: renderedTime,
      timeLabel: timeDisplay,
    });
  }
}

/** The scroll shadow at the top or bottom of a calendar's days. */
@component({
  name: "lona-calendar-days-row-shadow",
})
export class CalendarDaysStripShadow extends LonaWebComponent {
  static makeTop(): CalendarDaysStripShadow {
    const $e = CalendarDaysStripShadow.make();
    $e.assignStyles({
      top: "0px",
      "--box-shadow": "0px -6px 12px 6px var(--black50)",
    });
    return $e;
  }

  static makeBottom(): CalendarDaysStripShadow {
    const $e = CalendarDaysStripShadow.make();
    $e.assignStyles({
      bottom: "0px",
      "--box-shadow": "0px 6px 12px 3px var(--black50)",
    });
    return $e;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        position: absolute;
        width: 100%;
        z-index: 3000; /* ZIndex.STICKY */
      }

      :host::before {
        content: "";
        position: absolute;
        left: 10%;
        width: 80%;
        box-shadow: var(--box-shadow);
      }
    `,
  ];
}
